// apps/share-web/src/player-view/chart-panel.ts
//
// Раздвижная панель Chart & Lyrics. Основная колонка СЖИМАЕТСЯ, а не
// накрывается — так просил Сергей, и поэтому в chart.css анимируется ширина, а
// не transform: сдвиг накрыл бы микшер. Плата известна и осознана.
//
// В панели только тональность и две кнопки. Колонок нет: панель шириной 372 px,
// вторая колонка в ней нечитаема.
//
// А строка «сейчас → дальше» в шапке ЕСТЬ — Сергей попросил её и здесь («да, это
// нужно»). Прежняя причина её отсутствия («показывать её нечем, панель на тик не
// подписана») снялась вместе с кадром: панель гоняет его сама, и он тикает И
// ведение, И шапку. Кадр РОВНО ОДИН на поверхность, и с волны A.1 это
// утверждает сам интерфейс ведения: своего rAF у него больше нет вовсе, потому
// что два клока на одном листе давали двойной тик ведения каждый кадр. Тот же
// урок записан в шапке chart-fullscreen.ts.
// Сам ряд собирает chart-now-next.ts, общий со слоем: «дальше» в панели и
// «дальше» в слое обязаны быть одной правдой об одной песне.
//
// Кнопки — ДС-компоненты (`<bw-button>` через chart/chart-buttons.ts), а не свои
// `<button>` с рамкой: пункт приёмки A1-2, «кнопка должна быть кнопкой по
// дизайн-системе». Размер — из шкалы компонента (`size="sm"` = 28 px): панель
// смотрят с расстояния вытянутой руки, а не со стойки.
//
// Панель живёт ОДНУ песню: chart-follow забирает список секций при создании, а
// тональность считается от `songKey`. Смена песни — новый монтаж (mountPlayerView
// пересобирает поверхность целиком), поэтому setSong здесь нет: API, которого
// никто не зовёт, хуже отсутствующего.
//
// А вот сдвиг тональности панели НЕ принадлежит: владелец один на песню — блок
// чарта в player-view/chart-surfaces.ts, — потому что панель и полный экран это
// два окна в ОДИН чарт. Слой пересоздаётся на каждое открытие, и своя ячейка в замыкании
// означала бы ровно то, что показал замер ревью: панель печатает D, слой
// одновременно C, а «✕ и снова открыть» сбрасывает транспозицию. Отсюда пара
// `initialShift` / `onShiftChange` и `setShift` в ручке: панель ПОКАЗЫВАЕТ
// значение владельца и сообщает о выборе, но не помнит его сама.

import { leadSeconds } from '@bandway/chordpro';
import { renderChart } from '../chart/chart-render.js';
import {
  createChartFollow,
  domViewport,
  type ChartFollow,
  type ChartFollowDeps,
  type SectionSec,
  type PositionSource,
} from '../chart/chart-follow.js';
import { mountKeySelect, mountFontStepper, clampShift } from '../chart/chart-controls.js';
import { clampFontPct, fontScale } from '../chart/chart-font.js';
import { mountNowNext, type NowNextHandle } from '../chart/chart-now-next.js';
import { iconButton } from '../chart/chart-buttons.js';
import { bindEscape } from '../chart/chart-escape.js';
import { t } from '../ui/i18n.js';

export interface ChartPanelDeps {
  text: string;
  /**
   * Хранимые связи чарта из нагрузки. Без них русский чарт не находит ни одного
   * блока: связь выводилась бы по именам, а «Куплет 1» и «Verse 1» — разные
   * строки. Отсутствие законно (шейр до правки контракта).
   */
  links?: Record<string, string> | null;
  songKey: string;
  /** Темп песни; 0 = не задан (та же договорённость, что в контрактах шейров). */
  bpm?: number;
  /** Размер такта песни, «4/4». */
  timeSignature?: string;
  sections: SectionSec[];
  position: PositionSource;
  onFullscreen: () => void;
  onClose: () => void;
  /**
   * «Escape мой?» — то есть над панелью ничего не открыто. Полный экран стоит
   * ПОВЕРХ панели, и Escape там закрывает его, а не её: панель за слоем не видна
   * вовсе, и сложить её заодно значит вернуть читателя из чтения к панели,
   * которую он не складывал. Знает об этом вызывающий, он и отвечает.
   */
  canEscape?: () => boolean;
  /** Сдвиг владельца на момент монтажа. Своего у панели нет — см. шапку. */
  initialShift?: number;
  /** Читатель выбрал тональность здесь. Владелец обязан узнать: слой, открытый
   *  после этого, берёт начальное значение у него. */
  onShiftChange?: (shift: number) => void;
  /** Размер (проценты к базе) владельца на момент монтажа. Своего у панели нет — см. шапку. */
  initialFontPct?: number;
  /** Читатель сменил размер здесь. Владелец обязан узнать: слой, открытый
   *  после этого, берёт начальное значение у него. */
  onFontPctChange?: (pct: number) => void;
  /**
   * Шов ведения. В проде — настоящий createChartFollow; подменяется только в
   * тестах, и не ради изоляции, а потому что иначе замок 3 недоказуем: у
   * настоящего ведения первый замок «нет ширины — не считаем», а happy-dom
   * раскладку не считает вовсе, так что любая проверка удержания зеленела бы
   * на любой реализации. Своё состояние наружу панель не отдаёт — метод
   * «только для тестов» в публичном интерфейсе стоит дороже одного шва.
   */
  createFollow?: (opts: ChartFollowDeps) => ChartFollow;
}

export interface ChartPanelHandle {
  setOpen(open: boolean): void;
  isOpen(): boolean;
  /** Тональность сменили НЕ здесь (в полном экране). Панель обязана показать
   *  ту же: иначе выход из чтения возвращает читателя к прежним аккордам. */
  setShift(shift: number): void;
  /** Размер сменили НЕ здесь (в полном экране). Панель обязана показать тот же. */
  setFontPct(pct: number): void;
  destroy(): void;
}

/**
 * Страховка замка 3: длиннее перехода (.26s в chart.css) с запасом. Нужна
 * потому, что transitionend не приходит, если перехода не было ВОВСЕ —
 * prefers-reduced-motion, нулевая длительность, панель уже в этом состоянии.
 * Без неё ведение оставалось бы запертым до конца песни.
 */
const SETTLE_MS = 400;

const el = (tag: string, cls?: string, txt?: string): HTMLElement => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (txt != null) e.textContent = txt;
  return e;
};

export function mountChartPanel(host: HTMLElement, deps: ChartPanelDeps): ChartPanelHandle {
  let shift = clampShift(deps.initialShift);
  let fontPct = clampFontPct(deps.initialFontPct);
  let open = false;

  const root = el('div', 'cw-panel');
  const head = el('div', 'cw-panel-head');

  const titleRow = el('div', 'cw-panel-row');
  titleRow.appendChild(el('div', 'cw-panel-title', t('chart.open')));
  titleRow.appendChild(iconButton({
    glyph: '⤢',
    name: t('chart.fullscreen'),
    cls: 'cw-btn-fs',
    size: 'sm',
    onClick: () => deps.onFullscreen(),
  }));
  titleRow.appendChild(iconButton({
    glyph: '✕',
    name: t('chart.close'),
    cls: 'cw-btn-close',
    size: 'sm',
    onClick: () => deps.onClose(),
  }));

  const controlsRow = el('div', 'cw-panel-row');
  head.appendChild(titleRow);
  head.appendChild(controlsRow);

  // Строка «сейчас → дальше» — ТРЕТИЙ ряд шапки, ниже дропдауна: сведения о
  // песне стоят под её органами управления, как и в слое. Секций у песни нет —
  // ряда нет вовсе, и решает это сам модуль (`null` вместо ручки).
  const nowNext: NowNextHandle | null = mountNowNext(head, {
    sections: deps.sections,
    rowClass: 'cw-panel-nn',
  });

  const pane = el('div', 'cw-pane');
  root.appendChild(head);
  root.appendChild(pane);
  host.appendChild(root);

  // Множитель кегля живёт на пане — там же, где база (chart.css). Ставится ДО
  // первой отрисовки: лист, нарисованный на базовом кегле и перевёрстанный
  // следом, дал бы видимый скачок при каждом открытии панели.
  const applyFont = (): void => {
    pane.style.setProperty('--cw-fs-scale', String(fontScale(fontPct)));
  };

  // Ведение создаётся ДО draw, потому что draw о нём говорит. В черновике плана
  // порядок был обратный (draw объявлен выше follow) и работал случайно: тело
  // стрелки читает переменную в момент ВЫЗОВА, а первый вызов стоял ниже
  // присваивания. Одна переставленная строка — и это ReferenceError.
  const follow = (deps.createFollow ?? createChartFollow)({
    pane,
    sections: deps.sections,
    links: deps.links,
    position: deps.position,
    viewport: domViewport(pane),
    // Лид считает ЯДРО: перевод «два такта → секунды» живёт там же, где сама
    // константа LEAD_BARS, и второй арифметики здесь не заводим.
    leadSec: leadSeconds(deps.bpm ?? 0, deps.timeSignature),
  });

  const draw = (): void => {
    // Наличие слов проверяется ЗДЕСЬ, а не один раз при монтаже: замороженный
    // флаг — ловушка для первой же перерисовки под другой текст.
    const hasWords = (deps.text ?? '').trim().length > 0;
    pane.textContent = '';
    if (hasWords) renderChart(pane, { text: deps.text, shift, columns: 1 });
    // Пустой лист — призрак: занимает место и ловит прокрутку. Вместо него
    // честная строка о том, что слов у песни пока нет.
    else pane.appendChild(el('div', 'cw-empty', t('chart.empty')));
    // Лист пересобран — карта «секция → блок» внутри ведения устарела, и
    // подсветка иначе осталась бы на элементах, которых в DOM больше нет.
    follow.invalidate();
  };

  // Дропдаун монтируется функцией, потому что его зовут дважды: при сборке и из
  // setShift. Повторный монтаж заменяет прошлый НА МЕСТЕ (chart-controls.ts,
  // replaceChild), так что подпись пересчитывается, а порядок в шапке цел.
  const mountKey = (): void => {
    mountKeySelect(controlsRow, {
      songKey: deps.songKey,
      shift,
      onChange: (next) => { shift = next; deps.onShiftChange?.(next); draw(); },
    });
  };
  mountKey();

  const mountFont = (): void => {
    mountFontStepper(controlsRow, {
      pct: fontPct,
      onChange: (next) => {
        fontPct = next;
        deps.onFontPctChange?.(next);
        applyFont();
        // Перерисовка не нужна: кегль меняет только CSS, разметка листа та же.
        // А вот карта ведения устарела — высоты блоков другие.
        follow.invalidate();
      },
    });
  };
  mountFont();

  // Escape складывает панель — так закрывается всё остальное в share-web.
  // Сложенная панель клавишу не трогает: закрывать нечего, и сосед по document
  // (петля секции) обязан получить свой Escape.
  const unbindEscape = bindEscape(
    () => open && (deps.canEscape?.() ?? true),
    () => deps.onClose(),
  );

  // Читатель повёл лист сам — ведение молчит до следующей смены секции.
  for (const ev of ['wheel', 'touchstart', 'pointerdown'] as const) {
    pane.addEventListener(ev, () => follow.handOff(), { passive: true });
  }

  // ЗАМОК 3. Пока панель едет, её ширина промежуточная: строки переносятся
  // иначе, значит и вертикальные позиции другие. Замер на прототипе: прокрутка
  // уходила на 362 при ширине 355, перелетала на 924 и возвращалась на 414 —
  // читатель видел рывок вперёд и откат. Подсветку красит ведение независимо от
  // удержания (она от ширины не зависит), а прокрутка ждёт конца перехода.
  let animating = false;
  let settleTimer: ReturnType<typeof setTimeout> | null = null;

  const settle = (): void => {
    if (settleTimer != null) { clearTimeout(settleTimer); settleTimer = null; }
    if (!animating) return;
    animating = false;
    follow.setAnimating(false);
  };

  const onTransitionEnd = (e: Event): void => {
    // Переходы ДЕТЕЙ всплывают сюда: у .cw-blk своя анимация фона, и ведение,
    // перекрасив активный блок посреди раздвигания, отпускало бы удержание
    // собственной перекраской.
    if (e.target !== root) return;
    // Отпускать по opacity рано: она короче ширины (.2s против .26s), а до
    // финальной ширины строки переносятся иначе — ровно то, из-за чего замок и
    // появился. У синтетического Event в тестах propertyName нет; такое событие
    // считаем своим.
    const prop = (e as Event & { propertyName?: string }).propertyName;
    if (prop != null && prop !== '' && prop !== 'width' && prop !== 'flex-basis') return;
    settle();
  };
  root.addEventListener('transitionend', onTransitionEnd);

  /**
   * Один кадр поверхности: ведение листа и шапка. Кадр ОДИН — см. шапку модуля;
   * позиция при этом читается для шапки отдельно от ведения, ровно как в слое
   * (chart-fullscreen.ts), потому что ведению нужен ещё и признак «играет».
   */
  const tickOnce = (): void => {
    follow.tickOnce();
    nowNext?.sync(deps.position.now()?.posSec ?? null);
  };

  let raf: number | null = null;
  const loop = (): void => {
    tickOnce();
    raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame(loop) : null;
  };

  applyFont();
  draw();
  // Первый кадр — синхронно: панель раскрывают и на паузе посреди песни, шапка
  // обязана назвать секцию сразу. Это же единственный кадр там, где rAF нет.
  tickOnce();
  if (typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(loop);

  return {
    setOpen(next: boolean) {
      if (next === open) return;
      open = next;
      root.classList.toggle('is-open', open);
      animating = true;
      follow.setAnimating(true);
      // Один таймер на панель: два нажатия подряд иначе оставили бы два, и
      // первый отпускал бы удержание посреди второго перехода.
      if (settleTimer != null) clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, SETTLE_MS);
    },
    isOpen: () => open,
    setShift(next: number) {
      const v = clampShift(next);
      if (v === shift) return;
      shift = v;
      mountKey();
      draw();
    },
    setFontPct(next: number) {
      const v = clampFontPct(next);
      if (v === fontPct) return;
      fontPct = v;
      mountFont();
      applyFont();
      follow.invalidate();
    },
    destroy() {
      // Кадр уходит вместе с панелью: иначе rAF тикал бы по оторванному DOM до
      // конца сессии.
      if (raf != null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
      raf = null;
      nowNext?.destroy();
      // Таймер уходит вместе с панелью: сработав после destroy, он дёрнул бы
      // остановленное ведение.
      if (settleTimer != null) { clearTimeout(settleTimer); settleTimer = null; }
      root.removeEventListener('transitionend', onTransitionEnd);
      // Слушатель Escape сидит на document, то есть переживает свой host:
      // не снятый, он глотал бы клавишу до конца сессии.
      unbindEscape();
      root.remove();
    },
  };
}
